import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAnimationObject } from '../../../redux/Slice/animationSlice';
import store from '../../../redux/store';
import { TIMELINE_CONFIG } from '../constants/animationConstants';
import {
  areTimelineEffectListsEqual,
  groupTimelineEffectsByObjectId,
} from '../utils/timelineUtils';
import { isGridHistorySuppressed } from '../../../utils/gridHistorySuppress';

export const useAnimationState = (onMinimizeChange) => {
  const { animationObjects } = useSelector((state) => {
    const s = state.present ? state.present : state;
    return s.animation || {};
  });
  const { select_object } = useSelector((state) => {
    const s = state.present ? state.present : state;
    return s.assets_object || {};
  });
  const dispatch = useDispatch();
  const animationObjectsRef = useRef(animationObjects);
  animationObjectsRef.current = animationObjects;

  // After first hydrate from Redux (project load), local timeline state is authoritative.
  const timelineHydratedRef = useRef(false);
  const lastSyncedSignatureRef = useRef('');

  const getTimelineEffectsSignature = useCallback((effects = []) => {
    const sortById = (list) =>
      [...list].sort((a, b) => String(a.id).localeCompare(String(b.id)));
    return JSON.stringify(sortById(effects));
  }, []);

  // Basic state
  const [isMinimized, setIsMinimizedRaw] = useState(true);
  const [expandedItems, setExpandedItems] = useState(
    animationObjects.reduce((acc, obj) => ({ ...acc, [obj.id]: false }), {})
  );
  const [animationEffectsExpanded, setAnimationEffectsExpanded] =
    useState(true);
  const [animationSettingsExpanded, setAnimationSettingsExpanded] =
    useState(true);

  // Timeline state
  const [draggedEffect, setDraggedEffect] = useState(null);
  const [timelineEffects, setTimelineEffects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedEffects, setSelectedEffects] = useState([]);
  const [isDraggingOverTimeline, setIsDraggingOverTimeline] = useState(false);
  const [draggedTimelineEffect, setDraggedTimelineEffect] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Timeline scaling and bounds
  const [timelineScale, setTimelineScale] = useState(1);
  const [effectStartTime, setEffectStartTime] = useState(0);
  const [effectEndTime, setEffectEndTime] = useState(7);
  const [maxTimelineDuration, setMaxTimelineDuration] = useState(7);

  // Resizing state
  const [isDragging, setIsDragging] = useState(false);
  const [animationHeight, setAnimationHeight] = useState(
    TIMELINE_CONFIG.DEFAULT_HEIGHT
  );
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartHeight, setDragStartHeight] = useState(
    TIMELINE_CONFIG.DEFAULT_HEIGHT
  );
  const [isDraggingIndicator, setIsDraggingIndicator] = useState(false);

  // Current time indicator
  const [currentTimeIndicator, setCurrentTimeIndicator] = useState({
    position: 0,
    time: 0,
    visible: true,
  });

  // Animation settings
  const [animationSettings, setAnimationSettings] = useState({
    duration: 3.0,
    startTime: 0.0,
    easing: 'None',
    activeAxes: { x: false, y: false, z: false },
    startPosition: { x: 1, y: 0, z: 0 },
    endPosition: { x: 10, y: 0, z: 0 },
    loop: false,
    movementType: 'linear',
    circleCenter: { x: 0, y: 0, z: 0 },
    circleRadius: 5,
    circleAxis: 'z',
    circleDirection: 'clockwise',
    circleStartAngle: 0,
    circleEndAngle: 360,
    circleRevolutions: 1,
    bezierControl1: { x: 5, y: 5, z: 0 },
    bezierControl2: { x: 10, y: -5, z: 0 },
    sineAmplitude: { x: 2, y: 0, z: 0 },
    sineFrequency: 1,
    sinePhase: 0,
    sineAxis: 'x',
    figure8Radius: 5,
    figure8Axis: 'z',
    figure8Speed: 0.5,
    spiralRadius: 5,
    spiralHeight: 5,
    spiralTurns: 2,
    spiralAxis: 'z',
    spiralDirection: 'up',
    relativeToObject: false,
    followPath: false,
    pathSmoothing: 0.5,
    autoRotate: false,
    lookAtTarget: { x: 0, y: 0, z: 0 },
  });

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackStartTime, setPlaybackStartTime] = useState(null);
  const [playbackInitialIndicatorTime, setPlaybackInitialIndicatorTime] =
    useState(0);

  // ─── FIX: Wrap setIsMinimized so collapsing always resets height to DEFAULT ──
  const setIsMinimized = useCallback((val) => {
    setIsMinimizedRaw((prevMinimized) => {
      const nextMinimized =
        typeof val === 'function' ? val(prevMinimized) : val;

      if (nextMinimized === true) {
        setAnimationHeight(TIMELINE_CONFIG.DEFAULT_HEIGHT);
      }

      return nextMinimized;
    });
  }, []);

  // ─── Reset function ───────────────────────────────────────────────────────────
  const resetToInitialState = () => {
    setIsMinimizedRaw(true);
    setAnimationHeight(TIMELINE_CONFIG.DEFAULT_HEIGHT);
    setExpandedItems({});
    setAnimationEffectsExpanded(true);
    setAnimationSettingsExpanded(true);
    setDraggedEffect(null);
    setTimelineEffects([]);
    setSelectedObject(null);
    setSelectedEffects([]);
    setIsDraggingOverTimeline(false);
    setDraggedTimelineEffect(null);
    setDragOffset({ x: 0, y: 0 });
    setTimelineScale(1);
    setEffectStartTime(0);
    setEffectEndTime(7);
    setMaxTimelineDuration(7);
    setIsDragging(false);
    setDragStartY(0);
    setDragStartHeight(TIMELINE_CONFIG.DEFAULT_HEIGHT);
    setIsDraggingIndicator(false);
    setCurrentTimeIndicator({ position: 0, time: 0, visible: true });
    setAnimationSettings({
      duration: 3.0,
      startTime: 0.0,
      easing: 'None',
      activeAxes: { x: false, y: false, z: false },
      startPosition: { x: 1, y: 0, z: 0 },
      endPosition: { x: 10, y: 0, z: 0 },
      loop: false,
      movementType: 'linear',
      circleCenter: { x: 0, y: 0, z: 0 },
      circleRadius: 5,
      circleAxis: 'z',
      circleDirection: 'clockwise',
      circleStartAngle: 0,
      circleEndAngle: 360,
      circleRevolutions: 1,
      bezierControl1: { x: 5, y: 5, z: 0 },
      bezierControl2: { x: 10, y: -5, z: 0 },
      sineAmplitude: { x: 2, y: 0, z: 0 },
      sineFrequency: 1,
      sinePhase: 0,
      sineAxis: 'x',
      figure8Radius: 5,
      figure8Axis: 'z',
      figure8Speed: 0.5,
      spiralRadius: 5,
      spiralHeight: 5,
      spiralTurns: 2,
      spiralAxis: 'z',
      spiralDirection: 'up',
      relativeToObject: false,
      followPath: false,
      pathSmoothing: 0.5,
      autoRotate: false,
      lookAtTarget: { x: 0, y: 0, z: 0 },
    });
    setIsPlaying(false);
    setPlaybackStartTime(null);
    setPlaybackInitialIndicatorTime(0);
    timelineHydratedRef.current = false;
    lastSyncedSignatureRef.current = '';
  };

  // Apply Redux animationObjects → local timeline (used on load + undo/redo).
  const hydrateTimelineFromRedux = useCallback(() => {
    const objects = store.getState().animation?.animationObjects || [];
    const allEffects = objects.flatMap((obj) => obj.timelineEffects || []);
    setTimelineEffects(allEffects);
    lastSyncedSignatureRef.current = getTimelineEffectsSignature(allEffects);
    timelineHydratedRef.current = true;
    setSelectedEffects([]);
  }, [getTimelineEffectsSignature]);

  // ─── Event Listeners ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleResetAnimationState = () => resetToInitialState();
    const handleDeleteMesh = () => {
      setSelectedEffects([]);
      setEffectEndTime(3);
    };
    // Undo/redo restores Redux; timeline UI is local state — re-hydrate it.
    const handleHistoryRestored = () => {
      hydrateTimelineFromRedux();
    };

    window.addEventListener('resetAnimationState', handleResetAnimationState);
    window.addEventListener('deleteMesh', handleDeleteMesh);
    window.addEventListener('gridHistoryRestored', handleHistoryRestored);

    return () => {
      window.removeEventListener(
        'resetAnimationState',
        handleResetAnimationState
      );
      window.removeEventListener('deleteMesh', handleDeleteMesh);
      window.removeEventListener('gridHistoryRestored', handleHistoryRestored);
    };
  }, [hydrateTimelineFromRedux]);

  // ─── Sync expanded items when objects change ──────────────────────────────────
  useEffect(() => {
    setExpandedItems((prev) => {
      const next = animationObjects.reduce(
        (acc, obj) => ({ ...acc, [obj.id]: prev[obj.id] ?? false }),
        {}
      );
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [animationObjects]);

  // ─── One-time hydrate from Redux (project load) ─────────────────────────────
  useEffect(() => {
    if (timelineHydratedRef.current) return;

    const allEffects = animationObjects.flatMap(
      (obj) => obj.timelineEffects || []
    );

    if (allEffects.length > 0) {
      setTimelineEffects(allEffects);
      lastSyncedSignatureRef.current = getTimelineEffectsSignature(allEffects);
      timelineHydratedRef.current = true;
      return;
    }

    if (animationObjects.length > 0) {
      timelineHydratedRef.current = true;
    }
  }, [animationObjects, getTimelineEffectsSignature]);

  // ─── Sync local selection with global selection ───────────────────────────────
  useEffect(() => {
    let nextSelected = null;

    if (select_object && Object.keys(select_object).length > 0) {
      const selectedId =
        select_object.name ||
        select_object.id ||
        select_object._id ||
        select_object.uuid ||
        select_object.temp_file_name;
      const isAnimationObject = animationObjects.some(
        (obj) => obj.id === selectedId
      );
      if (isAnimationObject) {
        nextSelected = selectedId;
      }
    }

    setSelectedObject((prev) => (prev === nextSelected ? prev : nextSelected));
  }, [select_object, animationObjects]);

  // ─── Sync timeline effects to Redux (local → store, one-way) ──────────────────
  useEffect(() => {
    // Skip while undo/redo is applying Redux snapshots (avoids extra history steps).
    if (isGridHistorySuppressed()) return;

    const signature = getTimelineEffectsSignature(timelineEffects);
    if (signature === lastSyncedSignatureRef.current) return;

    const objects = animationObjectsRef.current;
    if (!objects.length) {
      lastSyncedSignatureRef.current = signature;
      return;
    }

    const groupedByObjectId = groupTimelineEffectsByObjectId(timelineEffects);

    objects.forEach((obj) => {
      const effects = groupedByObjectId[obj.id] || [];
      const currentEffects = obj.timelineEffects || [];

      if (!areTimelineEffectListsEqual(currentEffects, effects)) {
        dispatch(
          updateAnimationObject({
            id: obj.id,
            updates: { timelineEffects: effects },
          })
        );
      }
    });

    lastSyncedSignatureRef.current = signature;
  }, [timelineEffects, dispatch, getTimelineEffectsSignature]);

  // ─── Notify parent of minimize/height changes ─────────────────────────────────
  const onMinimizeChangeRef = useRef(onMinimizeChange);
  onMinimizeChangeRef.current = onMinimizeChange;

  useEffect(() => {
    const safeHeight = Math.min(
      TIMELINE_CONFIG.DEFAULT_HEIGHT,
      animationHeight
    );
    onMinimizeChangeRef.current(isMinimized, safeHeight, isDragging);
  }, [isMinimized, animationHeight, isDragging]);

  return {
    // Basic state
    isMinimized,
    setIsMinimized, // ← this is now the WRAPPED version with height reset built in
    expandedItems,
    setExpandedItems,
    animationEffectsExpanded,
    setAnimationEffectsExpanded,
    animationSettingsExpanded,
    setAnimationSettingsExpanded,

    // Timeline state
    draggedEffect,
    setDraggedEffect,
    timelineEffects,
    setTimelineEffects,
    selectedObject,
    setSelectedObject,
    selectedEffects,
    setSelectedEffects,
    isDraggingOverTimeline,
    setIsDraggingOverTimeline,
    draggedTimelineEffect,
    setDraggedTimelineEffect,
    dragOffset,
    setDragOffset,

    // Timeline scaling and bounds
    timelineScale,
    setTimelineScale,
    effectStartTime,
    setEffectStartTime,
    effectEndTime,
    setEffectEndTime,
    maxTimelineDuration,
    setMaxTimelineDuration,

    // Resizing state
    isDragging,
    setIsDragging,
    animationHeight,
    setAnimationHeight,
    dragStartY,
    setDragStartY,
    dragStartHeight,
    setDragStartHeight,
    isDraggingIndicator,
    setIsDraggingIndicator,

    // Current time indicator
    currentTimeIndicator,
    setCurrentTimeIndicator,

    // Animation settings
    animationSettings,
    setAnimationSettings,

    // Playback state
    isPlaying,
    setIsPlaying,
    playbackStartTime,
    setPlaybackStartTime,
    playbackInitialIndicatorTime,
    setPlaybackInitialIndicatorTime,
  };
};
